import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeft,
  Palette,
  Sun,
  Moon,
  Menu,
  ChevronUp,
  ChevronDown,
  Eye,
  EyeOff,
  RotateCcw,
  Check,
  Home,
  Users,
  FileText,
  LayoutPanelLeft,
  Share2,
  Image,
  BarChart3,
  CheckSquare,
  Package,
  Calendar,
  Sparkles,
  Languages,
  Wand2,
} from 'lucide-react';
import { useSettings, COLOR_PRESETS, DEFAULT_MENU_ORDER } from '../contexts/SettingsContext';
import { useButter, HUE_LABELS, STYLE_LABELS, type ButterHue, type ButterStyle } from '../contexts/ButterContext';
import { TourList } from '../components/onboarding';
import { SUPPORTED_LANGUAGES, type SupportedLanguage } from '../i18n';
import i18n from '../i18n';

// Menu item configuration
const MENU_ITEMS_CONFIG: Record<string, { label: string; icon: typeof Home }> = {
  dashboard: { label: 'Dashboard', icon: Home },
  todos: { label: 'To-Dos', icon: CheckSquare },
  calendar: { label: 'Kalender', icon: Calendar },
  leads: { label: 'Leads', icon: Users },
  products: { label: 'Produkte', icon: Package },
  forms: { label: 'Formulare', icon: FileText },
  'site-pages': { label: 'Meine Seiten', icon: LayoutPanelLeft },
  analytics: { label: 'Analytics', icon: BarChart3 },
  media: { label: 'Medien', icon: Image },
  'social-media': { label: 'Social Media', icon: Share2 },
};

export default function Einstellungen() {
  const { t } = useTranslation();
  const {
    primaryColor,
    themeMode,
    menuOrder,
    hiddenMenuItems,
    setPrimaryColor,
    setThemeMode,
    setMenuOrder,
    toggleMenuItemVisibility,
    resetMenuToDefault,
  } = useSettings();
  const { settings: butter, setStyle, setHue, setBlur, setAccent } = useButter();
  const [butterAccentInput, setButterAccentInput] = useState(butter.accent);

  const [customColor, setCustomColor] = useState(primaryColor);
  const [, setIsSaving] = useState(false);

  const handleLanguageChange = (code: SupportedLanguage) => {
    i18n.changeLanguage(code);
  };

  const handleColorChange = async (color: string) => {
    setIsSaving(true);
    try {
      await setPrimaryColor(color);
      setCustomColor(color);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCustomColorChange = async () => {
    if (customColor !== primaryColor) {
      await handleColorChange(customColor);
    }
  };

  const handleThemeChange = async (mode: 'light' | 'dark') => {
    setIsSaving(true);
    try {
      await setThemeMode(mode);
    } finally {
      setIsSaving(false);
    }
  };

  const moveMenuItem = async (index: number, direction: 'up' | 'down') => {
    const newOrder = [...menuOrder];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= newOrder.length) return;

    [newOrder[index], newOrder[newIndex]] = [newOrder[newIndex], newOrder[index]];
    await setMenuOrder(newOrder);
  };

  const handleResetMenu = async () => {
    setIsSaving(true);
    try {
      await resetMenuToDefault();
    } finally {
      setIsSaving(false);
    }
  };

  const isMenuModified =
    JSON.stringify(menuOrder) !== JSON.stringify(DEFAULT_MENU_ORDER) ||
    hiddenMenuItems.length > 0;

  return (
    <div className="space-y-6 pb-12">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Link
            to="/profile"
            className="p-2 hover:bg-gray-100 dark:hover:bg-slate-800 rounded-xl transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-[#6B6B6B] dark:text-slate-400" />
          </Link>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-[#1A1A1A] dark:text-white">
              {t('settings.title')}
            </h1>
            <p className="text-[#6B6B6B] dark:text-slate-400">
              {t('settings.subtitle')}
            </p>
          </div>
        </div>
      </div>

      {/* Primary Color */}
      <div className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-100 dark:border-slate-700 p-6 lg:p-8 shadow-sm">
        <div className="flex items-center gap-3 mb-6">
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center"
            style={{ backgroundColor: `${primaryColor}20` }}
          >
            <Palette className="w-5 h-5" style={{ color: primaryColor }} />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-[#1A1A1A] dark:text-white">
              Primärfarbe
            </h2>
            <p className="text-sm text-[#6B6B6B] dark:text-slate-400">
              Wähle deine Akzentfarbe für die App
            </p>
          </div>
        </div>

        {/* Color Presets */}
        <div className="grid grid-cols-4 sm:grid-cols-8 gap-3 mb-6">
          {COLOR_PRESETS.map((preset) => (
            <button
              key={preset.value}
              onClick={() => handleColorChange(preset.value)}
              className={`
                relative w-full aspect-square rounded-xl transition-all
                hover:scale-105 hover:shadow-lg
                ${primaryColor === preset.value ? 'ring-2 ring-offset-2 ring-gray-400 dark:ring-offset-slate-800' : ''}
              `}
              style={{ backgroundColor: preset.value }}
              title={preset.name}
            >
              {primaryColor === preset.value && (
                <Check className="absolute inset-0 m-auto w-5 h-5 text-white drop-shadow-md" />
              )}
            </button>
          ))}
        </div>

        {/* Custom Color */}
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <input
              type="color"
              value={customColor}
              onChange={(e) => setCustomColor(e.target.value)}
              className="w-10 h-10 rounded-lg cursor-pointer border-0"
            />
            <input
              type="text"
              value={customColor}
              onChange={(e) => setCustomColor(e.target.value)}
              className="w-28 px-3 py-2 bg-gray-50 dark:bg-slate-700 border border-gray-200 dark:border-slate-600 rounded-lg text-sm font-mono text-[#1A1A1A] dark:text-white"
              placeholder="#9B6BFF"
            />
          </div>
          {customColor !== primaryColor && (
            <button
              onClick={handleCustomColorChange}
              className="px-4 py-2 text-white rounded-lg text-sm font-medium transition-colors"
              style={{ backgroundColor: customColor }}
            >
              Anwenden
            </button>
          )}
        </div>
      </div>

      {/* Theme Mode */}
      <div className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-100 dark:border-slate-700 p-6 lg:p-8 shadow-sm">
        <div className="flex items-center gap-3 mb-6">
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center"
            style={{ backgroundColor: `${primaryColor}20` }}
          >
            {themeMode === 'dark' ? (
              <Moon className="w-5 h-5" style={{ color: primaryColor }} />
            ) : (
              <Sun className="w-5 h-5" style={{ color: primaryColor }} />
            )}
          </div>
          <div>
            <h2 className="text-lg font-semibold text-[#1A1A1A] dark:text-white">
              Erscheinungsbild
            </h2>
            <p className="text-sm text-[#6B6B6B] dark:text-slate-400">
              Wähle zwischen hellem und dunklem Design
            </p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <button
            onClick={() => handleThemeChange('light')}
            className={`
              flex items-center gap-4 p-4 rounded-xl border-2 transition-all
              ${
                themeMode === 'light'
                  ? 'border-current bg-gray-50'
                  : 'border-gray-200 dark:border-slate-600 hover:border-gray-300 dark:hover:border-slate-500'
              }
            `}
            style={themeMode === 'light' ? { borderColor: primaryColor } : {}}
          >
            <div
              className="w-12 h-12 rounded-xl flex items-center justify-center"
              style={{
                backgroundColor: themeMode === 'light' ? `${primaryColor}20` : undefined,
              }}
            >
              <Sun
                className="w-6 h-6"
                style={{ color: themeMode === 'light' ? primaryColor : '#6B6B6B' }}
              />
            </div>
            <div className="text-left">
              <p className="font-medium text-[#1A1A1A] dark:text-white">Hell</p>
              <p className="text-sm text-[#6B6B6B] dark:text-slate-400">
                Klassisches helles Design
              </p>
            </div>
          </button>

          <button
            onClick={() => handleThemeChange('dark')}
            className={`
              flex items-center gap-4 p-4 rounded-xl border-2 transition-all
              ${
                themeMode === 'dark'
                  ? 'border-current bg-slate-700'
                  : 'border-gray-200 dark:border-slate-600 hover:border-gray-300 dark:hover:border-slate-500'
              }
            `}
            style={themeMode === 'dark' ? { borderColor: primaryColor } : {}}
          >
            <div
              className="w-12 h-12 rounded-xl flex items-center justify-center"
              style={{
                backgroundColor: themeMode === 'dark' ? `${primaryColor}20` : undefined,
              }}
            >
              <Moon
                className="w-6 h-6"
                style={{ color: themeMode === 'dark' ? primaryColor : '#6B6B6B' }}
              />
            </div>
            <div className="text-left">
              <p className="font-medium text-[#1A1A1A] dark:text-white">Dunkel</p>
              <p className="text-sm text-[#6B6B6B] dark:text-slate-400">
                Augenschonend bei Nacht
              </p>
            </div>
          </button>
        </div>
      </div>

      {/* Menu Customization */}
      <div className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-100 dark:border-slate-700 p-6 lg:p-8 shadow-sm">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{ backgroundColor: `${primaryColor}20` }}
            >
              <Menu className="w-5 h-5" style={{ color: primaryColor }} />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-[#1A1A1A] dark:text-white">
                Menü anpassen
              </h2>
              <p className="text-sm text-[#6B6B6B] dark:text-slate-400">
                Ordne Menüpunkte an und blende sie aus
              </p>
            </div>
          </div>
          {isMenuModified && (
            <button
              onClick={handleResetMenu}
              className="flex items-center gap-2 px-3 py-1.5 text-sm text-[#6B6B6B] dark:text-slate-400 hover:text-[#1A1A1A] dark:hover:text-white hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
            >
              <RotateCcw className="w-4 h-4" />
              Zurücksetzen
            </button>
          )}
        </div>

        <div className="space-y-2">
          {menuOrder.map((key, index) => {
            const config = MENU_ITEMS_CONFIG[key];
            if (!config) return null;

            const Icon = config.icon;
            const isHidden = hiddenMenuItems.includes(key);

            return (
              <div
                key={key}
                className={`
                  flex items-center gap-3 p-3 rounded-xl border transition-all
                  ${
                    isHidden
                      ? 'bg-gray-50 dark:bg-slate-700/50 border-gray-200 dark:border-slate-600 opacity-60'
                      : 'bg-white dark:bg-slate-800 border-gray-200 dark:border-slate-600'
                  }
                `}
              >
                {/* Reorder buttons */}
                <div className="flex flex-col gap-0.5">
                  <button
                    onClick={() => moveMenuItem(index, 'up')}
                    disabled={index === 0}
                    className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronUp className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => moveMenuItem(index, 'down')}
                    disabled={index === menuOrder.length - 1}
                    className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronDown className="w-4 h-4" />
                  </button>
                </div>

                {/* Icon and label */}
                <div
                  className="w-8 h-8 rounded-lg flex items-center justify-center"
                  style={{ backgroundColor: isHidden ? undefined : `${primaryColor}20` }}
                >
                  <Icon
                    className="w-4 h-4"
                    style={{ color: isHidden ? '#6B6B6B' : primaryColor }}
                  />
                </div>
                <span
                  className={`flex-1 font-medium ${
                    isHidden
                      ? 'text-[#6B6B6B] dark:text-slate-500'
                      : 'text-[#1A1A1A] dark:text-white'
                  }`}
                >
                  {config.label}
                </span>

                {/* Visibility toggle */}
                <button
                  onClick={() => toggleMenuItemVisibility(key)}
                  className={`
                    p-2 rounded-lg transition-colors
                    ${
                      isHidden
                        ? 'text-gray-400 hover:text-gray-600 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-slate-600'
                        : 'hover:bg-gray-100 dark:hover:bg-slate-700'
                    }
                  `}
                  style={{ color: isHidden ? undefined : primaryColor }}
                  title={isHidden ? 'Einblenden' : 'Ausblenden'}
                >
                  {isHidden ? (
                    <EyeOff className="w-5 h-5" />
                  ) : (
                    <Eye className="w-5 h-5" />
                  )}
                </button>
              </div>
            );
          })}
        </div>

        <p className="text-xs text-[#6B6B6B] dark:text-slate-400 mt-4">
          Ausgeblendete Menüpunkte sind weiterhin über die URL erreichbar.
        </p>
      </div>

      {/* Language Selection */}
      <div className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-100 dark:border-slate-700 p-6 lg:p-8 shadow-sm">
        <div className="flex items-center gap-3 mb-6">
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center"
            style={{ backgroundColor: `${primaryColor}20` }}
          >
            <Languages className="w-5 h-5" style={{ color: primaryColor }} />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-[#1A1A1A] dark:text-white">
              {t('settings.language.title')}
            </h2>
            <p className="text-sm text-[#6B6B6B] dark:text-slate-400">
              {t('settings.language.description')}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {SUPPORTED_LANGUAGES.map((lang) => {
            const isSelected = i18n.language === lang.code || i18n.language?.startsWith(lang.code);
            return (
              <button
                key={lang.code}
                onClick={() => handleLanguageChange(lang.code)}
                className={`
                  flex items-center gap-3 p-3 rounded-xl border-2 transition-all text-left
                  ${
                    isSelected
                      ? 'border-current bg-gray-50 dark:bg-slate-700'
                      : 'border-gray-200 dark:border-slate-600 hover:border-gray-300 dark:hover:border-slate-500 hover:bg-gray-50 dark:hover:bg-slate-700/50'
                  }
                `}
                style={isSelected ? { borderColor: primaryColor } : {}}
              >
                <span className="text-2xl">{lang.flag}</span>
                <div className="min-w-0">
                  <p
                    className="font-medium text-sm truncate"
                    style={{ color: isSelected ? primaryColor : undefined }}
                  >
                    {lang.label}
                  </p>
                  <p className="text-xs text-[#6B6B6B] dark:text-slate-400 uppercase">{lang.code}</p>
                </div>
                {isSelected && (
                  <Check className="w-4 h-4 ml-auto flex-shrink-0" style={{ color: primaryColor }} />
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Onboarding Tours */}
      <div className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-100 dark:border-slate-700 p-6 lg:p-8 shadow-sm">
        <div className="flex items-center gap-3 mb-6">
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center"
            style={{ backgroundColor: `${primaryColor}20` }}
          >
            <Sparkles className="w-5 h-5" style={{ color: primaryColor }} />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-[#1A1A1A] dark:text-white">
              {t('settings.tours.title')}
            </h2>
            <p className="text-sm text-[#6B6B6B] dark:text-slate-400">
              {t('settings.tours.description')}
            </p>
          </div>
        </div>

        <TourList />
      </div>

      {/* Butter Glass Design */}
      <div className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-100 dark:border-slate-700 p-6 lg:p-8 shadow-sm">
        <div className="flex items-center gap-3 mb-6">
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center"
            style={{ background: 'var(--bt-accent-soft)' }}
          >
            <Wand2 className="w-5 h-5" style={{ color: 'var(--bt-accent)' }} />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-[#1A1A1A] dark:text-white">
              Glass-Design
            </h2>
            <p className="text-sm text-[#6B6B6B] dark:text-slate-400">
              Passe das Butter Glassmorphism-Design an
            </p>
          </div>
        </div>

        <div className="space-y-6">
          {/* Style Mode */}
          <div>
            <p className="text-sm font-medium text-[#1A1A1A] dark:text-white mb-3">Stil-Modus</p>
            <div className="grid grid-cols-3 gap-3">
              {(Object.keys(STYLE_LABELS) as ButterStyle[]).map((style) => (
                <button
                  key={style}
                  onClick={() => setStyle(style)}
                  className="p-3 rounded-xl border-2 transition-all text-sm font-medium"
                  style={{
                    borderColor: butter.style === style ? 'var(--bt-accent)' : 'var(--bt-line-soft)',
                    background: butter.style === style ? 'var(--bt-accent-soft)' : 'var(--bt-card)',
                    color: butter.style === style ? 'var(--bt-accent)' : 'var(--bt-mute)',
                    backdropFilter: 'blur(8px)',
                  }}
                >
                  {STYLE_LABELS[style]}
                </button>
              ))}
            </div>
          </div>

          {/* Hue */}
          <div>
            <p className="text-sm font-medium text-[#1A1A1A] dark:text-white mb-3">Farbton</p>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
              {(Object.keys(HUE_LABELS) as ButterHue[]).map((hue) => {
                const hueColors: Record<ButterHue, string> = {
                  lavender: 'linear-gradient(135deg, #c4b5fd, #fcd3e1, #bae6ff)',
                  peach: 'linear-gradient(135deg, #ffc8aa, #ffe1be, #e6d2ff)',
                  mint: 'linear-gradient(135deg, #b4e6d2, #c8e1ff, #dce6c8)',
                  rose: 'linear-gradient(135deg, #ffc8d2, #ffb4c8, #dcd2ff)',
                  sky: 'linear-gradient(135deg, #b4d2ff, #c8e6fa, #e6d2fa)',
                };
                return (
                  <button
                    key={hue}
                    onClick={() => setHue(hue)}
                    className="relative p-2 rounded-xl border-2 transition-all"
                    style={{
                      background: hueColors[hue],
                      borderColor: butter.hue === hue ? 'var(--bt-accent)' : 'transparent',
                    }}
                    title={HUE_LABELS[hue]}
                  >
                    <span className="text-xs font-medium block text-center" style={{ color: '#1a1726', textShadow: '0 1px 2px rgba(255,255,255,0.5)' }}>
                      {HUE_LABELS[hue]}
                    </span>
                    {butter.hue === hue && (
                      <div
                        className="absolute top-1 right-1 w-4 h-4 rounded-full flex items-center justify-center"
                        style={{ background: 'var(--bt-accent)' }}
                      >
                        <Check className="w-2.5 h-2.5 text-white" />
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Blur Slider */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-medium text-[#1A1A1A] dark:text-white">Glass-Blur</p>
              <span className="text-sm font-mono" style={{ color: 'var(--bt-accent)' }}>{butter.blur}px</span>
            </div>
            <input
              type="range"
              min={8}
              max={72}
              step={2}
              value={butter.blur}
              onChange={(e) => setBlur(Number(e.target.value))}
              className="w-full h-2 rounded-full appearance-none cursor-pointer"
              style={{
                background: `linear-gradient(to right, var(--bt-accent) 0%, var(--bt-accent) ${((butter.blur - 8) / 64) * 100}%, var(--bt-line-soft) ${((butter.blur - 8) / 64) * 100}%, var(--bt-line-soft) 100%)`,
              }}
            />
            <div className="flex justify-between text-xs mt-1" style={{ color: 'var(--bt-faint)' }}>
              <span>8px</span>
              <span>72px</span>
            </div>
          </div>

          {/* Accent Color */}
          <div>
            <p className="text-sm font-medium text-[#1A1A1A] dark:text-white mb-3">Akzentfarbe</p>
            <div className="flex items-center gap-3">
              <input
                type="color"
                value={butterAccentInput}
                onChange={(e) => setButterAccentInput(e.target.value)}
                onBlur={() => setAccent(butterAccentInput)}
                className="w-10 h-10 rounded-lg cursor-pointer border-0"
              />
              <input
                type="text"
                value={butterAccentInput}
                onChange={(e) => setButterAccentInput(e.target.value)}
                onBlur={() => setAccent(butterAccentInput)}
                className="w-28 px-3 py-2 rounded-lg text-sm font-mono"
                style={{
                  background: 'var(--bt-card)',
                  border: '1px solid var(--bt-glass-stroke)',
                  color: 'var(--bt-ink)',
                }}
                placeholder="#8b7ad1"
              />
              <div
                className="w-10 h-10 rounded-lg flex-shrink-0"
                style={{ background: butterAccentInput }}
              />
              <button
                onClick={() => setAccent('#8b7ad1')}
                className="text-xs px-3 py-2 rounded-lg transition-colors"
                style={{ background: 'var(--bt-card)', border: '1px solid var(--bt-glass-stroke)', color: 'var(--bt-mute)' }}
                title="Auf Standard zurücksetzen"
              >
                <RotateCcw className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>

          {/* Preview strip */}
          <div
            className="rounded-xl p-4 flex items-center gap-4"
            style={{ background: 'var(--bt-card)', border: '1px solid var(--bt-glass-stroke)', backdropFilter: 'blur(var(--bt-blur))' }}
          >
            <div className="flex gap-2">
              <div className="w-8 h-8 rounded-lg bt-glass" style={{ border: '1px solid var(--bt-glass-stroke)' }} />
              <div className="w-8 h-8 rounded-lg" style={{ background: 'var(--bt-accent)', boxShadow: '0 4px 14px -4px var(--bt-accent-soft)' }} />
              <div className="w-8 h-8 rounded-lg bt-glass-dark" />
            </div>
            <div>
              <p className="text-sm font-medium" style={{ color: 'var(--bt-ink)' }}>Vorschau</p>
              <p className="text-xs" style={{ color: 'var(--bt-mute)' }}>
                {STYLE_LABELS[butter.style]} · {HUE_LABELS[butter.hue]} · {butter.blur}px
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
